import { Popover } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { Navlinks } from "./Navlinks";
import getButtonStyle from "../../utils/styles";
import { ChevronUpIcon, Bars3Icon } from "@heroicons/react/24/outline";
import { headerLinks } from "../../utils/links";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { logout } from "../../slices/auth";
import { generateAlertText } from "../../utils/boilerplate";
import { addAlert } from "../../slices/alert";
import Link from "../layout/Link";
import MobileNavLink from "../layout/MobileNavLink";
import Spinner from "../layout/Spinner";
import { translateText } from "../../utils/translation";
import SpaceFiller from "../layout/SpaceFiller";

export default function TopNavbar() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, loading, waiting } = useAppSelector(
    (state) => state.auth,
  );
  const language = !user ? "EN" : user.language;
  const groups = !user ? [] : user.auth.groups.map((x) => x[1]);

  async function triggerLogout(e: any) {
    e.preventDefault();
    const res: any = await dispatch(logout());
    if (res.payload.status === 205) {
      dispatch(
        addAlert(generateAlertText("logout", language), "success", 5000),
      );
    } else {
      dispatch(addAlert(generateAlertText("logout_error", language), "danger"));
    }
  }

  function getHomePageForUser() {
    const baseUrl = "/feed";
    if (groups.includes("Feed - Full app access")) return baseUrl;
    if (groups.includes("Rooms - Full app access")) return "/rooms/overview";
    if (groups.includes("Request templates - Full app access"))
      return "/rooms/templates";
    if (groups.includes("Devices - Full app access")) return "/devices";
    if (groups.includes("Insights - Full app access"))
      return "/insights/analytics";
    if (groups.includes("Communication - Full app access"))
      return "/communication/announcements";
    if (groups.includes("Content - Full app access"))
      return "/content/campaigns";
    if (groups.includes("Integration - Full app access"))
      return "/integration/configuration";
    if (groups.includes("Smart control - Full app access"))
      return "/smart-control/appliances";
    if (groups.includes("Payment - Full app access")) return "/payment/items";
    if (groups.includes("Property - Full app access"))
      return "/property/catalog";
    if (groups.includes("Settings - Full app access"))
      return "/settings/profile";
    if (groups.includes("Tasks - Full app access")) return "/tasks/manage";
    if (groups.includes("Runner")) return baseUrl;
    if (groups.includes("Marketing")) return "/communication/announcements";
    if (groups.includes("Family and friends")) return "/insights/analytics";
    if (groups.includes("Admin")) return baseUrl;
    if (groups.includes("Executive")) return "/oversight/overview";
    if (groups.includes("audico staff")) return baseUrl;
    if (groups.includes("Endpoint")) return baseUrl;
    return "/feed";
  }

  return (
    <header className="sticky top-0 z-50 bg-midnight drop-shadow-sm">
      <nav>
        <div className="relative z-50 mx-auto flex max-w-7xl justify-between px-4 py-8 sm:px-6 lg:px-8 border-b border-white/20">
          <div className="relative z-10 flex items-center gap-16">
            {/* Logo button */}
            <a href="/" aria-label="Home">
              <span className="sr-only">audico</span>
              <img
                className="h-10 w-auto"
                src="/img/logo/detail_gradient_light.svg"
                alt="audico home logo"
              />
            </a>

            {/* Nav links */}
            <div className="hidden lg:flex lg:gap-10">
              <Navlinks links={headerLinks} colour="hoverTransparent" />
            </div>
          </div>

          <div className="flex items-center gap-6">
            {/* Dropdown menu for small viewports */}
            <Popover className="lg:hidden">
              {({ open }) => (
                <>
                  <Popover.Button
                    className="relative z-10 -m-2 inline-flex items-center rounded-lg stroke-gray-900 p-4 hover:bg-slate-100/20 hover:stroke-gray-600 active:stroke-gray-900 [&:not(:focus-visible)]:focus:outline-none"
                    aria-label="Toggle site navigation"
                  >
                    {({ open }) =>
                      open ? (
                        <ChevronUpIcon className="h-6 w-6 text-white" />
                      ) : (
                        <Bars3Icon className="h-6 w-6 text-white" />
                      )
                    }
                  </Popover.Button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <>
                        <Popover.Overlay
                          static
                          as={motion.div}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="fixed inset-0 z-0 bg-midnight-light backdrop-blur"
                        />
                        <Popover.Panel
                          static
                          as={motion.div}
                          initial={{ opacity: 0, y: -32 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{
                            opacity: 0,
                            y: -32,
                            transition: { duration: 0.2 },
                          }}
                          className="absolute inset-x-0 top-0 z-0 origin-top rounded-b-2xl bg-midnight-light px-6 pb-6 pt-32 shadow-2xl shadow-gray-900/20"
                        >
                          {/* Mobile nav links */}
                          <div className="space-y-4">
                            {headerLinks.map((x: any[], idx: any) =>
                              MobileNavLink({
                                key: idx,
                                href: x[1],
                                text: x[0],
                                className: "text-light hover:bg-slate-100/10",
                              }),
                            )}
                          </div>
                          {!loading && (
                            <div className="mt-8 flex flex-col gap-4">
                              {!isAuthenticated ? (
                                <Link
                                  className={`${getButtonStyle(
                                    "outline",
                                    "gray",
                                  )} text-light`}
                                  href="/login"
                                >
                                  Login
                                </Link>
                              ) : (
                                <>
                                  {waiting ? (
                                    <SpaceFiller>
                                      <Spinner size={5} />
                                    </SpaceFiller>
                                  ) : (
                                    <Link
                                      className={`${getButtonStyle(
                                        "outline",
                                        "gray",
                                      )} text-light`}
                                      href="#!"
                                      onClick={(e) => triggerLogout(e)}
                                    >
                                      {translateText("logout", language)}
                                    </Link>
                                  )}
                                </>
                              )}
                              {user || isAuthenticated ? (
                                <Link
                                  href="/feed"
                                  className={`${getButtonStyle(
                                    "solid",
                                    "info",
                                  )}`}
                                >
                                  {translateText("go_to_app", language)}
                                </Link>
                              ) : null}
                            </div>
                          )}
                        </Popover.Panel>
                      </>
                    )}
                  </AnimatePresence>
                </>
              )}
            </Popover>

            {/* Full size action buttons in navbar */}
            {!loading && (
              <>
                {!isAuthenticated ? (
                  <Link
                    href="/login"
                    className={`hidden lg:block ${getButtonStyle(
                      "outline",
                      "gray",
                    )} text-light`}
                  >
                    Login
                  </Link>
                ) : (
                  <>
                    {waiting ? (
                      <SpaceFiller>
                        <Spinner size={5} />
                      </SpaceFiller>
                    ) : (
                      <Link
                        href="#!"
                        onClick={(e) => triggerLogout(e)}
                        className={`hidden lg:block ${getButtonStyle(
                          "outline",
                          "gray",
                        )} text-light`}
                      >
                        {translateText("logout", language)}
                      </Link>
                    )}
                  </>
                )}
                {user || isAuthenticated ? (
                  <Link
                    href={getHomePageForUser()}
                    className={`hidden lg:block ${getButtonStyle(
                      "solid",
                      "info",
                    )}`}
                  >
                    Go to app
                  </Link>
                ) : null}
              </>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}
